import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, Modal, Select, Space, Transfer } from "antd";
import { ColumnsType } from "antd/lib/table";
import { TableRowSelection } from "antd/lib/table/interface";
import { TransferItem } from "antd/lib/transfer";
import SortableTable from "components/SortableTable";
import { Roles } from "context/auth";
import { useAuthenticatedRequest, useAuthenticatedRequestCreator } from "hooks/useRequests";
import useUsers from "hooks/useUsers";
import _ from "lodash";
import { BaseEntity, InitialPageRequest } from "models/common";
import { ShipmentBatch } from "models/shipping";
import { useState } from "react";

interface BatchAssignmentModalProps {
    onClose: (assignmentsMade: boolean) => Promise<void>;
}

const columns: ColumnsType<AssignmentItem> = [
    {
        title: "Name",
        dataIndex: "title",
    },
    {
        title: "Items",
        dataIndex: "description",
        align: 'right'
    },
];

const useUnassignedBatches = () => {
    const get = useAuthenticatedRequest<ShipmentBatch[]>({
        method: "get",
        url: "/shipping/batches/unassigned"
    });

    const assign = useAuthenticatedRequestCreator<void, { userId: string, shipmentBatchIds: string[] }>(
        (data) => ({
            method: "post",
            url: "/shipping/batches/assign",
            data
        })
    );

    return {
        unassignedBatches: useQuery(["shipping", "batches", "unassigned"], get),
        assignBatches: useMutation(assign)
    };
}

type AssignmentItem = TransferItem & BaseEntity;

const BatchAssignmentModal = ({ onClose }: BatchAssignmentModalProps) => {
    const [open, setOpen] = useState(false);
    const {
        unassignedBatches: {
            data = [],
            isLoading,
            refetch: refreshBatches
        },
        assignBatches: {
            mutateAsync: assign,
            isLoading: isAssigning
        }
    } = useUnassignedBatches();
    const [targetKeys, setTargetKeys] = useState<string[]>([]);
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
    const { data: users, isLoading: isLoadingUsers } = useUsers({
        ...InitialPageRequest,
        pageSize: 100,
        filterBy: { 'Roles': [Roles.Staff, Roles.SeniorStaff], 'Active': [true] },
        sort: ['FirstName', true]
    });
    const [assignmentsMade, setAssignmentsMade] = useState(false);

    const batches: AssignmentItem[] = data.map(x => ({
        id: x.id,
        key: x.id,
        title: x.name,
        description: x.items.toLocaleString(),
        disabled: false,
    }));

    const assignBatchesToUser = async () => {
        await assign({
            userId: selectedUserId!,
            shipmentBatchIds: targetKeys
        });
        setSelectedUserId(null);

        await refreshBatches();
        setTargetKeys([]);
        setAssignmentsMade(true);
    }

    const isWorking = isLoading || isLoadingUsers || isAssigning;

    return <>
        <Button type="link" onClick={() => setOpen(true)} size="large">Assign Batches</Button>
        <Modal
            open={open}
            onOk={async () => {
                await onClose(assignmentsMade);
                setOpen(false);
                setTargetKeys([]);
                setAssignmentsMade(false);
                setSelectedUserId(null);
            }}
            okText="Close"
            cancelButtonProps={{ style: { display: 'none' } }}
            width='900px'
            closable={false}
            confirmLoading={isWorking}
        >
            <Space style={{ width: '100%', display: 'flex' }} direction="vertical">
                <Space style={{ width: '100%', justifyContent: 'end' }}>
                    <Select
                        size="large"
                        style={{ width: '316px' }}
                        value={selectedUserId}
                        onChange={setSelectedUserId}
                        loading={isLoadingUsers}
                        options={users?.data?.map(u => ({
                            label: `${u.firstName} ${u.lastName}`,
                            value: u.id
                        }))}
                    />
                    <Button
                        size="large"
                        type="primary"
                        disabled={selectedUserId === null || targetKeys.length === 0}
                        onClick={assignBatchesToUser}>
                        Assign
                    </Button>
                </Space>
                <Transfer
                    dataSource={batches}
                    onChange={(tk: string[]) => {
                        setTargetKeys(tk);
                    }}
                    targetKeys={targetKeys}
                    titles={['Available', 'Selected']}
                    disabled={isWorking}
                >
                    {({
                        direction,
                        filteredItems,
                        onItemSelectAll,
                        onItemSelect,
                        selectedKeys: listSelectedKeys,
                        disabled: listDisabled,
                    }) => {
                        const rowSelection: TableRowSelection<AssignmentItem> = {
                            getCheckboxProps: item => ({ disabled: listDisabled || item.disabled }),
                            onSelectAll(selected, selectedRows) {
                                const treeSelectedKeys = selectedRows
                                    .filter(item => !item.disabled)
                                    .map(({ key }) => key);
                                const diffKeys = selected
                                    ? _.difference(treeSelectedKeys, listSelectedKeys)
                                    : _.difference(listSelectedKeys, treeSelectedKeys);
                                onItemSelectAll(diffKeys as string[], selected);
                            },
                            onSelect({ key }, selected) {
                                onItemSelect(key as string, selected);
                            },
                            selectedRowKeys: listSelectedKeys,
                        };
                        const isTarget = direction === 'right';
                        const totalCards = batches.filter(x => targetKeys.some(tk => x.id === tk))
                            .map(x => Number(x.description))
                            .reduce((prev, cur) => cur + prev, 0);

                        return (<>
                            <SortableTable
                                isLoading={isLoading}
                                onReorder={(items) => Promise.resolve().then(() => setTargetKeys(items.map(x => x.id)))}
                                rowSelection={rowSelection}
                                columns={columns}
                                data={filteredItems}
                                size="small"
                                pagination={{ pageSize: 15 }}
                                style={{ pointerEvents: listDisabled ? 'none' : undefined }}
                                onRow={({ key, disabled: itemDisabled }) => ({
                                    onClick: () => {
                                        if (itemDisabled || listDisabled) return;
                                        onItemSelect(key as string, !listSelectedKeys.includes(key as string));
                                    },
                                })}
                                allowReorder={isTarget}
                                footer={isTarget && totalCards > 0 ?
                                    () => <div style={{ fontWeight: 'bold', display: 'flex', justifyContent: 'end' }}>
                                        {totalCards.toLocaleString()}
                                    </div>
                                    : undefined}
                            />
                        </>);
                    }}
                </Transfer>
            </Space>
        </Modal>
    </>;
};

export default BatchAssignmentModal;